import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Spinner,
  Switch,
  Textarea,
  useToast,
} from '../../../ui/components';
import { IconButton } from '../../../ui/components/Button';
import Avatar from '../../../ui/components/Avatar';
import { Icon } from '../../../ui/icons';
import { useT } from '../../../i18n';
import { suppliersApi } from '../suppliersApi';
import { formatDateTime } from './utils';

export default function NotesTab({ supplierId, onChanged }) {
  const toast = useToast();
  const t = useT();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setNotes(await suppliersApi.listNotes(supplierId));
    } catch (err) {
      toast.error(t('Could not load notes'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [supplierId, toast, t]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSaving(true);
    try {
      await suppliersApi.addNote(supplierId, { body: body.trim(), isPinned: pinned });
      setBody(''); setPinned(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not add note'), { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (note) => {
    try {
      await suppliersApi.updateNote(supplierId, note.id, { body: note.body, isPinned: !note.isPinned });
      await load();
    } catch (err) {
      toast.error(t('Could not update note'), { description: err?.message });
    }
  };

  const saveEdit = async (note) => {
    if (!editBody.trim()) return;
    try {
      await suppliersApi.updateNote(supplierId, note.id, { body: editBody.trim(), isPinned: note.isPinned });
      setEditingId(null); setEditBody('');
      await load();
    } catch (err) {
      toast.error(t('Could not save note'), { description: err?.message });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await suppliersApi.removeNote(supplierId, deleteTarget.id);
      setDeleteTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(t('Could not remove note'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="sp-panel">
      <Card>
        <Card.Body>
          <form className="sp-form" onSubmit={add}>
            <Textarea
              placeholder={t('Add a note about this supplier…')}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              autoGrow
              maxRows={8}
            />
            <div className="sp-section-head" style={{ marginBottom: 0 }}>
              <Switch checked={pinned} onChange={setPinned} label={t('Pin to top')} />
              <Button type="submit" variant="primary" loading={saving} disabled={!body.trim()} leadingIcon={<Icon.Plus size={15} />}>
                {t('Add note')}
              </Button>
            </div>
          </form>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="sp-center"><Spinner size={24} /></div>
      ) : notes.length === 0 ? (
        <EmptyState icon={<Icon.FileText size={28} />} title={t('No notes yet')} description={t('Capture call summaries, issues and reminders here.')} />
      ) : (
        <div className="sp-list">
          {notes.map((n) => (
            <div key={n.id} className={`sp-note ${n.isPinned ? 'is-pinned' : ''}`.trim()}>
              <div className="sp-note__head">
                <span className="sp-note__author">
                  <Avatar name={n.authorName || t('User')} size="xs" />
                  {n.authorName || t('User')}
                  {n.isPinned && <Badge tone="brand" dot>{t('Pinned')}</Badge>}
                </span>
                <span className="sp-note__time">{formatDateTime(n.createdAt)}</span>
              </div>

              {editingId === n.id ? (
                <div className="sp-form">
                  <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={3} autoGrow maxRows={8} />
                  <div className="sp-form__actions">
                    <Button type="button" variant="ghost" onClick={() => { setEditingId(null); setEditBody(''); }}>{t('Cancel')}</Button>
                    <Button type="button" variant="primary" onClick={() => saveEdit(n)} disabled={!editBody.trim()}>{t('Save')}</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="sp-note__body">{n.body}</div>
                  <div className="sp-item__actions" style={{ marginTop: 'var(--space-2)' }}>
                    <IconButton aria-label={n.isPinned ? t('Unpin') : t('Pin')} variant="ghost" size="icon-sm" onClick={() => togglePin(n)}>
                      <Icon.Bookmark size={15} />
                    </IconButton>
                    <IconButton aria-label={t('Edit note')} variant="ghost" size="icon-sm" onClick={() => { setEditingId(n.id); setEditBody(n.body); }}>
                      <Icon.Pencil size={15} />
                    </IconButton>
                    <IconButton aria-label={t('Delete note')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(n)}>
                      <Icon.Trash size={15} />
                    </IconButton>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
        onConfirm={handleDelete}
        title={t('Delete note')}
        description={t('Delete this note? This cannot be undone.')}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
      />
    </div>
  );
}
